import { EventBusDebugger } from 'shared/widgets/EventBusDebugger'
import { createContext, useContext, useRef, useEffect, useState, useCallback } from 'react'
import { eventBus, EventBus } from 'shared/lib/eventBus'

interface EventBusContextValue {
    bus: EventBus
    debugMode: boolean
    activeEvents: string[]
    emit: <T = any>(event: string, data?: T) => void
}

const EventBusContext = createContext<EventBusContextValue | null>(null)

interface EventBusProviderProps {
    children: React.ReactNode
    debug?: boolean
    enableLogging?: boolean
}

export const EventBusProvider = (props: EventBusProviderProps) => { 
    const {
        children, 
        debug = false,
        enableLogging = false 
    } = props

    const [activeEvents, setActiveEvents] = useState<string[]>([])
    const busRef = useRef(eventBus)
    const loggingRef = useRef(enableLogging)

    useEffect(() => {
        if (!debug) {
            return
        }

        const interval = setInterval(() => {
            setActiveEvents(busRef.current.getActiveEvents())
        }, 1000)

        return () => clearInterval(interval)
    }, [
        debug
    ])

    const emit = useCallback((event: string, data?: any) => {
        if (loggingRef.current) {
            console.log(`[EventBus] Emitting: ${event}`, data)
            console.trace(`Event: ${event} emitted from:`)
        }
        
        busRef.current.emit(event, data)
    } ,[

    ])

    const value: EventBusContextValue = {
        bus: busRef.current,
        debugMode: debug,
        activeEvents,
        emit,
    }

    return (
        <EventBusContext.Provider value={value}>
            {children}
            {debug && <EventBusDebugger />}
        </EventBusContext.Provider>
    )
}

// Custom hook with error handling
export const useEventBus = () => {
  const context = useContext(EventBusContext);
  if (!context) {
    throw new Error('useEventBus must be used within EventBusProvider');
  }
  return context;
};

// Type-safe event hook
export function useEventListener<T = any>(
  eventName: string,
  handler: (data: T) => void,
  dependencies: any[] = []
) {
  const { bus } = useEventBus();
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const wrappedHandler = (data: T) => {
      handlerRef.current(data);
    };
    
    const unsubscribe = bus.on(eventName, wrappedHandler);
    return unsubscribe;
  }, [eventName, bus, ...dependencies]);
}

// Once event hook
export function useEventOnce<T = any>(
  eventName: string,
  handler: (data: T) => void,
  dependencies: any[] = []
) {
  const { bus } = useEventBus();

  useEffect(() => {
    const unsubscribe = bus.once(eventName, handler);
    return unsubscribe;
  }, [eventName, bus, handler, ...dependencies]);
}